'use client'
import type { ChainId } from '@morpho-org/blue-sdk'
import type { Address } from 'viem'

import { isDefined } from '@morpho-org/morpho-ts'
import { CursorPagination } from '@morpho-org/ui-v6/client'
import { useTable } from '@tanstack/react-table'
import { startTransition, useCallback, useMemo, useState } from 'react'

import {
  DateRangeFilter,
  dateRangeToTimestamps,
  type DateRangeValue
} from '@/components/common/DateRangeFilter'
import { TableScrollArea } from '@/components/common/Table/CompactTable/Table'
import { TransactionTypeFilter } from '@/components/common/TransactionTypeFilter'
import { VaultTransactionsLayout } from '@/components/VaultPage/components/VaultTransactionsLayout'
import {
  getVaultTransactionsColumns,
  PAGE_SIZE,
  VAULT_V2_TRANSACTION_FILTER_OPTIONS as TRANSACTION_FILTER_OPTIONS,
  VAULT_V2_TO_VAULT_TRANSACTION_TYPE,
  VAULT_V2_TRANSACTION_TYPE_LABELS
} from '@/components/VaultPage/vaultTransactions/config'
import { useVaultTransactionsTableData } from '@/components/VaultPage/vaultTransactions/hooks/useVaultTransactionsTableData'
import { toVaultTransactionsTableRows } from '@/components/VaultPage/vaultTransactions/toVaultTransactionsTableRows'
import VaultV2TransactionsTableContent from '@/components/VaultPage/VaultV2/components/VaultV2TransactionsTableContent'
import { useIsBelowBreakpoint } from '@/hooks/useBreakpoints'
import { useSkeletonRows } from '@/hooks/useSkeletonRows'
import { VaultV2TransactionType } from '@/types/enums'

import { vaultTransactionsTableFeatures } from '../../../vaultTransactions/config/VaultTransactions.columns'

export interface VaultV2TransactionsClientProps {
  address: Address
  chainId: ChainId
  title: string
}

const columnsDefs = getVaultTransactionsColumns({ userColumn: 'dashboard' })

const VaultV2TransactionsClient = ({ address, chainId, title }: VaultV2TransactionsClientProps) => {
  const [transactionTypeFilter, setTransactionTypeFilter] = useState<string[]>([])
  const [dateRange, setDateRange] = useState<DateRangeValue>()

  const { timestampGte, timestampLte } = dateRangeToTimestamps(dateRange)

  const activeTransactionTypes = useMemo(
    () =>
      transactionTypeFilter
        .map(
          transactionType =>
            // oxlint-disable-next-line typescript/consistent-type-assertions
            VaultV2TransactionType[transactionType as keyof typeof VaultV2TransactionType]
        )
        .filter(isDefined),
    [transactionTypeFilter]
  )

  const {
    page,
    isLoading,
    isLoadingNextPage,
    error,
    pageIndex,
    previousPage,
    nextPage,
    canPreviousPage,
    canNextPage,
    prefetchNext
  } = useVaultTransactionsTableData({
    address,
    chainId,
    vaultVersion: 'v2',
    limit: PAGE_SIZE,
    filteredTransactionTypes: activeTransactionTypes,
    typeMap: VAULT_V2_TO_VAULT_TRANSACTION_TYPE,
    scope: { kind: 'activity' },
    timestampGte,
    timestampLte
  })

  const isMediumScreen = useIsBelowBreakpoint('m')

  const formattedTransactions = toVaultTransactionsTableRows({
    page,
    chainId,
    typeLabels: VAULT_V2_TRANSACTION_TYPE_LABELS,
    userAddressMode: 'onBehalf'
  })

  const handleFilterChange = useCallback((selectedTransactionTypes: string[]) => {
    startTransition(() => {
      setTransactionTypeFilter(selectedTransactionTypes)
    })
  }, [])

  const transactionsTable = useTable({
    features: vaultTransactionsTableFeatures,
    data: formattedTransactions,
    columns: columnsDefs,
    enableSorting: false
  })

  // First-load + next-page advance — see MarketTransactions.client for rationale.
  const loading = isLoading || isLoadingNextPage

  const loadingRows = useSkeletonRows({
    first: PAGE_SIZE,
    totalCount: PAGE_SIZE,
    pagination: { pageIndex: 0, pageSize: PAGE_SIZE },
    columnsDefs
  })

  return (
    <VaultTransactionsLayout
      title={title}
      tableSettingsSlot={
        <div className="gap-sm max-lg:gap-xs flex flex-wrap items-center">
          <DateRangeFilter
            value={dateRange}
            onChange={setDateRange}
            side="bottom"
            align="end"
            page="vault_activity"
          />
          {isMediumScreen ? (
            <div className="gap-xs flex flex-row items-center">
              <span className="text-3xs text-foreground">Type:</span>
              <TransactionTypeFilter
                selectedTypes={transactionTypeFilter}
                onFilterChange={handleFilterChange}
                options={TRANSACTION_FILTER_OPTIONS}
                side="bottom"
                align="end"
                page="vault_activity"
              />
            </div>
          ) : (
            <TransactionTypeFilter
              selectedTypes={transactionTypeFilter}
              onFilterChange={handleFilterChange}
              options={TRANSACTION_FILTER_OPTIONS}
              side="bottom"
              align="end"
              page="vault_activity"
            />
          )}
        </div>
      }
    >
      <div className="gap-lg flex h-[100%] w-[100%] flex-col">
        <div className="gap-xs flex flex-col">
          <TableScrollArea className="grid h-full w-full rounded-[20px]">
            <VaultV2TransactionsTableContent
              table={transactionsTable}
              loading={loading}
              error={error}
              partial={page?.partial ?? false}
              loadingRows={loadingRows}
              hasActiveFilters={transactionTypeFilter.length > 0 || Boolean(dateRange)}
              clearFilters={() => {
                handleFilterChange([])
                setDateRange(undefined)
              }}
            />
          </TableScrollArea>
          <CursorPagination
            loading={loading}
            error={error}
            pageIndex={pageIndex}
            previousPage={previousPage}
            nextPage={nextPage}
            canPreviousPage={canPreviousPage}
            canNextPage={canNextPage}
            onPrefetchNext={prefetchNext}
          />
        </div>
      </div>
    </VaultTransactionsLayout>
  )
}

export default VaultV2TransactionsClient
